import { useCallback } from 'react';
import type { UserRole } from '@cavaquinho/shared';
import {
  DEV_AUTH_AVAILABLE,
  DEV_ROLES,
  DEV_ROLE_LABELS,
  devRole,
  setDevRole,
} from '../../app/dev-auth.js';
import { Button } from '../../ui/primitives/Button.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './dev-auth.module.css';

/**
 * The development door (app/dev-auth.ts): the strip that lets a developer
 * change who they are pretending to be, once a role has been chosen.
 *
 * Renders nothing outside `pnpm dev`, and Vite drops it from a production
 * bundle along with everything it imports from `dev-auth.ts`. Not part of
 * the product: scaffolding for reviewing screens while the platform is being
 * built.
 */

/**
 * A choice is applied by reloading rather than by re-rendering.
 *
 * `AuthProvider` resolves the account once, at the top of the tree, on
 * purpose — so no navigation re-asks who is signed in. Reloading is how a
 * change of person is honoured without giving that provider a second way to
 * be told, which is exactly the kind of seam a development-only affordance
 * should not open in production code.
 */
function apply(role: UserRole | null, destination: string): void {
  setDevRole(role);
  window.location.assign(destination);
}

/**
 * The strip inside the application shell: who you are pretending to be, and
 * the two ways out of it. Present only once a role has actually been chosen,
 * so a real Google session is never shadowed by a control it has no use for.
 */
export function DevRoleSwitcher() {
  const current = devRole();

  const switchTo = useCallback((role: UserRole | null) => {
    apply(role, role === null ? '/entrar' : window.location.pathname);
  }, []);

  if (!DEV_AUTH_AVAILABLE || current === null) return null;

  return (
    <div className={styles.bar} data-testid="dev-role-switcher">
      <Text as="span" variant="body-sm" tone="muted">
        Modo de desenvolvimento · {DEV_ROLE_LABELS[current]}
      </Text>
      <div className={styles.choices}>
        {DEV_ROLES.filter((role) => role !== current).map((role) => (
          <span key={role} data-testid={`dev-switch-${role}`}>
            <Button variant="ghost" onClick={() => switchTo(role)}>
              Ver como {DEV_ROLE_LABELS[role].toLowerCase()}
            </Button>
          </span>
        ))}
        <span data-testid="dev-sign-out">
          <Button variant="ghost" onClick={() => switchTo(null)}>
            Sair
          </Button>
        </span>
      </div>
    </div>
  );
}
